import * as React from "react";
import { Form, Accordion, Icon, Dropdown, Table, Popup } from "semantic-ui-react";
import { convertFormattedValueString } from "../../utils/utils";
import { decodeValueDataFormat } from "../../utils/pdApiUtils";
import NumberField from "../../components/Editing/NumberField";

export type PreferredValuesProps = {
  values: number[];
  type: number;
  decimals: number;
  handleValuesChanged: (values: number[]) => void;
  handleTypeChanged: (type: number) => void;
  permissions: boolean;
  valueDataFormat: number;
  /** Validation error message to display. Empty string or undefined means no error. */
  validationError?: string;
};

// Contants for preferredValueTypeEnumValue
const preferredTypeMinMax = 1;
const preferredTypeEnumValues = 2;

type PreferredValuesState = {
  isOpen: boolean;
};

/** PreferredValues component.
 * Includes editing of `preferredValueTypeEnumValue` and `preferredValues` properties of
 * the drive parameter.
 */
class PreferredValues extends React.Component<PreferredValuesProps, PreferredValuesState> {
  state = { isOpen: false };
  render(): JSX.Element {
    const { type, handleTypeChanged, decimals, permissions, validationError } = this.props;
    const { isOpen } = this.state;
    const values = this.props.values;
    const decodedValue = decodeValueDataFormat(this.props.valueDataFormat);

    const typeOptions = [
      {
        key: 1,
        text: "PARAM_MIN_MAX",
        value: preferredTypeMinMax
      },
      {
        key: 2,
        text: "PARAM_ENUM_VALUES",
        value: preferredTypeEnumValues
      }
    ];

    return (
      <Form.Group grouped={true}>
        <Form.Field>
          <Accordion styled={true} className="edit-accordion">
            <Accordion.Title
              id="preferredValues"
              active={isOpen}
              onClick={() => this.setState(() => ({ isOpen: !isOpen }))}
              aria-describedby={validationError ? "preferred-values-error" : undefined}
            >
              <Icon name="dropdown" />
              <label>
                <b>Preferred values</b>
              </label>
              {!isOpen && validationError && (
                <p
                  className="errorMessagePadding"
                  role="alert"
                  aria-live="polite"
                  id="preferred-values-error"
                >
                  {validationError}
                </p>
              )}
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Form.Field
                id="valueType"
                label="Type"
                control={Dropdown}
                compact={true}
                fluid={true}
                selection={true}
                value={type}
                options={typeOptions}
                // eslint-disable-next-line
                onChange={({}, data: any) => handleTypeChanged(data.value)}
                disabled={permissions}
              />
              {type === preferredTypeMinMax ? (
                <Table collapsing={true}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{null}</Table.HeaderCell>
                      <Table.HeaderCell>Unformatted value</Table.HeaderCell>
                      <Table.HeaderCell>Formatted value</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  {values.length >= 2 ? (
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell>Min</Table.Cell>
                        <Table.Cell>
                          <div className="edit-number">
                            <NumberField
                              id="minValue"
                              value={values[0]}
                              isError={false}
                              onChange={value => {
                                this.handleNumberType(value.toString(), 0);
                              }}
                              disabled={permissions}
                              minValue={decodedValue.type === "Signed" ? "" : 0}
                            />
                          </div>
                        </Table.Cell>
                        <Table.Cell id="formattedMinValue">
                          {convertFormattedValueString(values[0], decimals)}
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Max</Table.Cell>
                        <Table.Cell>
                          <div className="edit-number">
                            <NumberField
                              id="maxValue"
                              value={values[1]}
                              isError={false}
                              onChange={value => {
                                this.handleNumberType(value.toString(), 1);
                              }}
                              disabled={permissions}
                              minValue={decodedValue.type === "Signed" ? "" : 0}
                            />
                          </div>
                        </Table.Cell>
                        <Table.Cell id="formattedMaxValue">
                          {convertFormattedValueString(values[1], decimals)}
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  ) : (
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell colSpan="3">Less than two values</Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  )}
                  {validationError && (
                    <Table.Footer>
                      <Table.Row className="validation-error-row">
                        <Table.Cell colSpan="3">
                          <p className="errorMsgDecPadding">{validationError}</p>
                        </Table.Cell>
                      </Table.Row>
                    </Table.Footer>
                  )}
                </Table>
              ) : (
                <Table collapsing={true}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Unformatted value</Table.HeaderCell>
                      <Table.HeaderCell>Formatted value</Table.HeaderCell>
                      {!permissions && (
                        <Table.HeaderCell>
                          <Popup
                            trigger={
                              <Icon
                                id="addValue"
                                className="color-green"
                                link={true}
                                name="add"
                                onClick={this.handleAddValue}
                              />
                            }
                            content={"Add new defaults"}
                          />
                        </Table.HeaderCell>
                      )}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {values.map((preferredValue, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <div className="edit-number">
                            <NumberField
                              id={`value-${index}`}
                              value={preferredValue}
                              isError={false}
                              onChange={value => {
                                this.handleNumberType(value.toString(), index);
                              }}
                              disabled={permissions}
                              minValue={decodedValue.type === "Signed" ? "" : 0}
                            />
                          </div>
                        </Table.Cell>
                        <Table.Cell id={`formattedValue-${index}`}>
                          {convertFormattedValueString(preferredValue, decimals)}
                        </Table.Cell>
                        {!permissions && (
                          <Table.Cell>
                            <Popup
                              trigger={
                                <Icon
                                  id={`removeValue-${index}`}
                                  className="color-red"
                                  link={true}
                                  name="remove"
                                  onClick={() => this.handleRemoveValue(index)}
                                />
                              }
                              content={"Remove value"}
                              disabled={permissions}
                            />
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ))}
                  </Table.Body>
                  {validationError && (
                    <Table.Footer>
                      <Table.Row className="validation-error-row">
                        <Table.Cell colSpan={!permissions ? "3" : "2"}>
                          <p className="errorMsgDecPadding">{validationError}</p>
                        </Table.Cell>
                      </Table.Row>
                    </Table.Footer>
                  )}
                </Table>
              )}
            </Accordion.Content>
          </Accordion>
        </Form.Field>
      </Form.Group>
    );
  }

  // Remove value handler
  private handleRemoveValue = (removedIndex: number) => {
    const { values, handleValuesChanged } = this.props;
    const modifiedValues = values.filter((_, index) => index !== removedIndex);
    handleValuesChanged(modifiedValues);
  };

  // Add value handler
  private handleAddValue = () => {
    const { values, handleValuesChanged } = this.props;
    handleValuesChanged([...values, 0]);
  };

  // Value changed handler
  private handleValuesChanged = (index: number, valueAsString: string) => {
    const { values, handleValuesChanged } = this.props;
    const updatedValues = [...values];
    updatedValues[index] = parseInt(valueAsString, 10);
    handleValuesChanged(updatedValues);
  };

  private handleNumberType = (value: string, index: number) => {
    const { valueDataFormat } = this.props;

    const decodedValue = decodeValueDataFormat(valueDataFormat);
    if (decodedValue.range === "Number") {
      if (decodedValue.digits) {
        if (Math.abs(Number(value)).toString().length <= decodedValue.digits) {
          this.handleValuesChanged(index, value);
        }
      } else {
        // digits = 0: only allow the value 0
        if (Number(value) === 0) {
          this.handleValuesChanged(index, value);
        }
      }
    } else {
      this.handleValuesChanged(index, value);
    }
  };
}

export default PreferredValues;
